import { createElement, type ComponentType } from "react";
import type { RouteObject } from "react-router-dom";
import { ALLOWED_BRAND_SLUGS } from "./brandRoute";
import { ALLOWED_CITY_SLUGS, CITY_ROUTE_BASE_PATH } from "./cityRoute";
import { APPLIANCE_CITY_PRERENDER_ENTRIES } from "./applianceRoute";
import { faqItems } from "../pages/faq/data";
import { postsCollection } from "../pages/posts/data";

const CITY_PRERENDER_ENTRIES: readonly VikePrerenderEntry[] = ALLOWED_CITY_SLUGS.map((slug) => ({
  url: `${CITY_ROUTE_BASE_PATH}/${slug}`,
  params: { city: slug },
}));

const POSTS_PRERENDER_ENTRIES: readonly VikePrerenderEntry[] = postsCollection.map((post) => ({
  url: `/posts/${post.slug}`,
  params: { slug: post.slug },
}));

type ModuleWithDefault = { default: ComponentType<unknown> };
type PageLoader = () => Promise<ModuleWithDefault>;

export interface VikePrerenderEntry {
  url: string;
  params?: Record<string, string>;
}

export interface VikePageDescriptor {
  id: string;
  path: string;
  load: PageLoader;
  prerenderEntries: readonly VikePrerenderEntry[];
  serverComponent?: ComponentType<unknown>;
  clientComponent?: ComponentType<unknown>;
}

const createDescriptor = (
  descriptor: Omit<VikePageDescriptor, "prerenderEntries"> & {
    prerenderEntries?: readonly VikePrerenderEntry[];
  }
): VikePageDescriptor => ({
  ...descriptor,
  prerenderEntries: descriptor.prerenderEntries ?? [],
});

const descriptors: readonly VikePageDescriptor[] = [
  createDescriptor({ id: "reschedule", path: "/reschedule-appointment", load: () => import("../pages/reschedule/page") }),
  createDescriptor({
    id: "home",
    path: "/",
    load: () => import("../pages/home/page"),
    prerenderEntries: [{ url: "/" }],
  }),
  createDescriptor({
    id: "appointment",
    path: "/appointment",
    load: () => import("../pages/home/page"),
    prerenderEntries: [{ url: "/appointment" }],
  }),
  createDescriptor({
    id: "ai-appointment",
    path: "/ai-appointment",
    load: () => import("../pages/ai-appointment/page"),
    prerenderEntries: [{ url: "/ai-appointment" }],
  }),
  createDescriptor({
    id: "city",
    path: `${CITY_ROUTE_BASE_PATH}/:city`,
    load: () => import("../pages/city/page"),
    prerenderEntries: CITY_PRERENDER_ENTRIES,
  }),
  createDescriptor({
    id: "faq",
    path: "/faq",
    load: () => import("../pages/faq/page"),
    prerenderEntries: [{ url: "/faq" }],
  }),
  createDescriptor({
    id: "faq-slug",
    path: "/faq/:slug",
    load: () => import("../pages/faq/page"),
    prerenderEntries: faqItems.map((item) => ({
      url: `/faq/${item.id}`,
      params: { slug: item.id },
    })),
  }),
  createDescriptor({
    id: "posts",
    path: "/posts",
    load: () => import("../pages/posts/page"),
    prerenderEntries: [{ url: "/posts" }],
  }),
  createDescriptor({
    id: "post-detail",
    path: "/posts/:slug",
    load: () => import("../pages/posts/detailPage"),
    prerenderEntries: POSTS_PRERENDER_ENTRIES,
  }),
  createDescriptor({
    id: "appliance-city",
    path: "/:applianceCitySlug",
    load: () => import("../pages/appliance/page"),
    prerenderEntries: APPLIANCE_CITY_PRERENDER_ENTRIES,
  }),
  createDescriptor({
    id: "privacy",
    path: "/privacy",
    load: () => import("../pages/privacy/page"),
    prerenderEntries: [{ url: "/privacy" }],
  }),
  createDescriptor({
    id: "terms-of-service",
    path: "/terms-of-service",
    load: () => import("../pages/terms/page"),
    prerenderEntries: [{ url: "/terms-of-service" }],
  }),
  createDescriptor({
    id: "websitemap",
    path: "/websitemap",
    load: () => import("../pages/websitemap/page"),
    prerenderEntries: [{ url: "/websitemap" }],
  }),
  createDescriptor({
    id: "licenses",
    path: "/licenses",
    load: () => import("../pages/licenses/page"),
    prerenderEntries: [{ url: "/licenses" }],
  }),
  createDescriptor({
    id: "brand",
    path: "/brand/:brand/*",
    load: () => import("../pages/brand/page"),
    prerenderEntries: ALLOWED_BRAND_SLUGS.map((slug) => ({
      url: `/brand/${slug}`,
      params: { brand: slug },
    })),
  }),
  createDescriptor({
    id: "not-found",
    path: "*",
    load: () => import("../pages/NotFound"),
  }),
];

const isServer = typeof window === "undefined";

const createRouteComponent = (descriptor: VikePageDescriptor): ComponentType<unknown> => {
  if (isServer) {
    const ServerComponent: ComponentType<unknown> = (props) => {
      if (!descriptor.serverComponent) {
        throw descriptor
          .load()
          .then((module) => {
            descriptor.serverComponent = module.default;
          })
          .catch((error) => {
            console.error(`Failed to load route component for ${descriptor.path}`, error);
            throw error;
          });
      }

      return createElement(descriptor.serverComponent, props);
    };

    return ServerComponent;
  }

  let loadPromise: Promise<void> | null = null;

  const ensureClientComponent = () => {
    if (descriptor.clientComponent) {
      return null;
    }

    if (!loadPromise) {
      loadPromise = descriptor
        .load()
        .then((module) => {
          descriptor.clientComponent = module.default;
        })
        .catch((error) => {
          loadPromise = null;
          throw error;
        });
    }

    return loadPromise;
  };

  const ClientComponent: ComponentType<unknown> = (props) => {
    if (!descriptor.clientComponent) {
      const promise = ensureClientComponent();
      if (promise) {
        throw promise;
      }

      if (!descriptor.clientComponent) {
        throw new Error(`Failed to load route component for ${descriptor.path}`);
      }
    }

    return createElement(descriptor.clientComponent, props);
  };

  return ClientComponent;
};

const routes: RouteObject[] = descriptors.map((descriptor) => ({
  path: descriptor.path,
  Component: createRouteComponent(descriptor),
  handle: {
    vike: descriptor,
  },
}));

export const vikePages: readonly VikePageDescriptor[] = descriptors;

export default routes;
